import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Row, Col, Form, Modal, Card, Container, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useIsMounted } from "components/Common/CustomHooks/useIsMounted";
import { useWindowSize } from "components/Common/CustomHooks/useWindowSize";

import conxtdOut from "apis/conxtdOut";
import Icon from 'components/Icons/Icon';

import { getSiteAssetViewUid } from 'services/redux/selectors/serviceDesk/assets';

import { Tooltip } from "components/Common/Tooltip/Tooltip";
import CopyToClipboard from "components/Site/SiteView/SiteOverview/Tabs/CopyToClipboard";
import { formatDateTimeData } from '../DateTimeFormatting/formatDateTimeData';
import DateTimeFormatUtility from '../DateTimeFormatting/DateTimeFormatUtility';
import CustomField from './CustomField';

import AssetLinking from "./AssetLinking";

import { isEqual, isObject } from 'lodash';

import Axios from '../../../../../../node_modules/axios/index';

import "../AssetEditor.scss";
import CustomFieldDropdown from "./CustomFieldDropdown";
import UserActionsChat from "./UserActionsChat";
import UnreadMessagesIcon from "./UnreadMessagesIcon";
import ButterToast, { POS_BOTTOM, POS_RIGHT, SCHEME_YELLOW } from 'butter-toast';

// Enlarged photo zoom bounds — 1 (100%) is the image fitted to its frame
const PHOTO_ZOOM_MIN = 0.25;
const PHOTO_ZOOM_MAX = 3;
const PHOTO_ZOOM_STEP = 0.25;

// SiteAssetView component (main component) ------------------------------->
function SiteAssetView({
  setLoadingSiteViewData, 
  timezone,
  setNavSiteViewName,
  setNavName,
  setSiteAssetView,
  scrollToTop,
  editMode,
  setEditMode,
  setChangeDetected,
  changeDetected,
  editData,
  setEditData,
  initialFormData,
  setInitialFormData,
  setTooltipDescription,
  setInvalid,
  forceReload,
  siteViewData,
  setSiteViewData,
  setShowEditButton,
}) {

  // selectors ------------------------------------------------------------>
  const darkMode = useSelector((state) => state.app.darkMode)
  const assetUid = useSelector(getSiteAssetViewUid);
  //-----------------------------------------------------------------------|

  // state ---------------------------------------------------------------->
  const isMounted = useIsMounted();
  const [renderOnLoad, setRenderOnLoad] = useState(true);
  const [nextMaintenanceDate, setNextMaintenanceDate] = useState('');
  const [defaultFields, setDefaultFields] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [name, setName] = useState({});
  const [photo, setPhoto] = useState('');
  const [newPhotoFile, setNewPhotoFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [photoFileInvalid, setPhotoFileInvalid] = useState(false);
  // const [invalid, setInvalid] = useState(false);
  // date-time formatting
  const [dateTimes, setDateTimes] = useState({});
  const [created, setCreated] = useState('');
  const [modified, setModified] = useState('');
  // modal state
  const [showAssetPhotoModal, setShowAssetPhotoModal] = useState(false);
  const [photoFullScreen, setPhotoFullScreen] = useState(false);
  const [photoZoom, setPhotoZoom] = useState(1);
  const [photoOffset, setPhotoOffset] = useState({ x: 0, y: 0 });
  const [draggingPhoto, setDraggingPhoto] = useState(false);
  // pointer position the current drag started from, less the offset already
  // applied — kept in a ref as it changes on every mouse move
  const photoDragOrigin = useRef(null);
  const [dataTypesAligned, setDataTypesAligned] = useState(false);

  // user actions chat section
  const [showUserActionChat, setShowUserActionChat] = useState(false);
  const [chatFullScreen, setChatFullScreen] = useState(false);
  const [hoveredSidePaneButton, setHoveredSidePaneButton] = useState(false);
  const [lastUpdatedActionsLog, setLastUpdatedActionsLog] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // opening the chat marks everything as read
  useEffect(() => {
    if (showUserActionChat) setUnreadCount(0);
  }, [showUserActionChat]);

  const toggleShowUserActionChat = () => {
    // chatFullScreen is deliberately left untouched here so the chat reopens
    // in the same (full screen or side pane) mode it was closed in
    setShowUserActionChat(!showUserActionChat);
  }

  // date-time formatting props ---------------------------------------------->
  const types = useMemo(() => [["created"], ["modified"]], []);
  const createdMemo = useMemo(
    () => [[created]], 
    [created]
  );
  const modifiedMemo = useMemo(
    () => [[modified]], 
    [modified]
  );
  //-----------------------------------------------------------------------|

  useEffect(() => {
    setSiteAssetView(true);
    setShowEditButton(true);

    return () => {
      setSiteAssetView(false);
      setShowEditButton(false);
    }
  }, [])

  const windowSize = useWindowSize({
    customBreakpoints: {
      md: 700,
    }
  });
  
  const resetPhotoView = () => {
    setPhotoZoom(1);
    setPhotoOffset({ x: 0, y: 0 });
  }

  const enlargePhoto = () => {
    resetPhotoView();
    setShowAssetPhotoModal(true);
  }

  const minimisePhoto = () => {
    setShowAssetPhotoModal(false);
    // photoFullScreen is deliberately left untouched here so the image reopens
    // in the same (full screen or standard) mode it was closed in
    resetPhotoView();
  }

  const zoomPhoto = (step) => {
    setPhotoZoom((zoom) => {
      // rounded to avoid floating point drift showing e.g. 74% instead of 75%
      const next = Math.round((zoom + step) * 100) / 100;
      return Math.min(PHOTO_ZOOM_MAX, Math.max(PHOTO_ZOOM_MIN, next));
    });
  }

  const toggleFullScreenPhoto = () => {
    // the frame changes size, so any pan the user had built up no longer
    // relates to what's on screen
    setPhotoOffset({ x: 0, y: 0 });
    setPhotoFullScreen(!photoFullScreen);
  }

  // drag the enlarged image around its frame to reposition it. The offset is
  // stored rather than scrolled so the image can be moved at any zoom level,
  // including when it's smaller than the frame
  const startPhotoDrag = (e) => {
    const point = e.touches ? e.touches[0] : e;
    // stops the browser's native image drag competing with ours
    if (!e.touches) e.preventDefault();

    photoDragOrigin.current = {
      x: point.clientX - photoOffset.x,
      y: point.clientY - photoOffset.y,
    };
    setDraggingPhoto(true);
  }

  useEffect(() => {
    if (!draggingPhoto) return;

    const movePhoto = (e) => {
      const origin = photoDragOrigin.current;
      if (!origin) return;

      const point = e.touches ? e.touches[0] : e;
      setPhotoOffset({
        x: point.clientX - origin.x,
        y: point.clientY - origin.y,
      });
      // stops the page scrolling behind the modal on touch devices
      if (e.cancelable) e.preventDefault();
    };
    const endPhotoDrag = () => setDraggingPhoto(false);

    // listening on the window keeps the drag alive when the pointer leaves the
    // image (or the modal) mid-drag
    window.addEventListener("mousemove", movePhoto);
    window.addEventListener("mouseup", endPhotoDrag);
    window.addEventListener("touchmove", movePhoto, { passive: false });
    window.addEventListener("touchend", endPhotoDrag);
    window.addEventListener("touchcancel", endPhotoDrag);

    return () => {
      window.removeEventListener("mousemove", movePhoto);
      window.removeEventListener("mouseup", endPhotoDrag);
      window.removeEventListener("touchmove", movePhoto);
      window.removeEventListener("touchend", endPhotoDrag);
      window.removeEventListener("touchcancel", endPhotoDrag);
    };
  }, [draggingPhoto])

  const onFileChange = (e) => {
    let photoFile = e.target.files[0]

    // file to be submitted
    setNewPhotoFile(photoFile);

    // preview new image in view
    const imageUrl = photoFile ? URL.createObjectURL(photoFile) : null;
    setPreviewImage(imageUrl);

    // cleanup old image conversions
    return () => URL.revokeObjectURL(imageUrl);
  };

  useEffect(() => {
    if (newPhotoFile) {
      const { size, type } = newPhotoFile;
      if (size > 2 * 1024 * 1024) {
        setPhotoFileInvalid("File is too large")
      } else if (!["image/png", "image/jpeg"].includes(type)) {
        setPhotoFileInvalid(
          <>
            File is not an image format that we accept. Suggested formats are:
            <span className="font-italic font-weight-bold ml-1">jpeg, png</span>
          </>
        )
      } else {
        setPhotoFileInvalid(false);
      }
    } else {
      setPhotoFileInvalid(false)
    }
  }, [newPhotoFile])

  // retreives site view data for this asset from backend ---------------->
  const getSiteViewData = useCallback(() => {
    const source = Axios.CancelToken.source();

    if (isMounted) {
      setLoadingSiteViewData(true);
      const getData = conxtdOut.get(`/company-asset-values/site-asset-view/${assetUid}`, { cancelToken: source.token });
      getData.then((response) => {
        // unconditional: setting state to its current value is a no-op in
        // React, so the old `if (renderOnLoad)` guard bought nothing — but
        // reading renderOnLoad made it a dependency of this callback, which is
        // what caused the double fetch (see the dependency list below)
        setRenderOnLoad(false);
        setLoadingSiteViewData(false);
        setSiteViewData(response.data)
      }).catch((error) => {
        console.log(error)
        setLoadingSiteViewData(false);
        setRenderOnLoad(false);
      });
    }

    return () => {
      source.cancel("Component unmounted: request canceled");
    }
    // renderOnLoad deliberately omitted: this callback sets it false on
    // completion, so including it re-created the callback, which re-ran the
    // effect below (it depends on this identity) and fetched a second time.
    // forceReload is kept — changing it is the intentional refetch trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetUid, setLoadingSiteViewData, forceReload, isMounted])

  useEffect(() => {
    if (assetUid && isMounted) {
      getSiteViewData();
    } 
  }, [assetUid, getSiteViewData, isMounted])
  // -----------------------------------------------------------------------|

  // exits "Edit Mode" if this component is unmounted
  useEffect(() => {
    setEditMode(false);
  }, [setEditMode])
  
  // updates nav name on change to name value
  useEffect(() => {
    if (isMounted && initialFormData && Array.isArray(initialFormData)) {
      const nameField = initialFormData?.find((f) => f.field_name === "Name") ?? null;
      const name = isObject(nameField) ? nameField?.value : nameField;
      setNavSiteViewName(name ?? "Site Asset");
    }
  }, [editMode, initialFormData, isMounted])

  useEffect(() => {
    (editMode && chatFullScreen) && setShowUserActionChat(false);
  }, [chatFullScreen, editMode])
  //-----------------------------------------------------------------------|

  // sets the tooltip description for the edit-mode footer submit-button-validation
  useEffect(() => {
    setTooltipDescription(
      <ul className="m-0 pl-3 text-left">
        {!changeDetected ? <li key={1}>no changes have been made</li> : null}
        {!name?.value ? <li key={2}>required field(s) are empty</li> : null}
        {photoFileInvalid ? <li key={3}>image file is invalid</li> : null}
      </ul>
    );
    setInvalid(!name?.value || photoFileInvalid || !changeDetected)
  }, [setTooltipDescription, name?.value, setInvalid, changeDetected, photoFileInvalid])
  //-----------------------------------------------------------------------|

  // resets edit data to initialised data upon exit of edit-mode 
  // - once it has validated that the correct data-type has been loaded, to avoid errors (e.g. array methods executed on objects)
  useEffect(() => {
    setDataTypesAligned(Object.prototype.toString.call(initialFormData) === Object.prototype.toString.call(editData));
  }, [editData, initialFormData])

  useEffect(() => {
    if (dataTypesAligned) {
      if (initialFormData && !isEqual(initialFormData, editData) && !editMode) {
        setEditData(initialFormData)
        setNewPhotoFile(null);
        setPreviewImage(null);
      }
    }
  }, [dataTypesAligned, editMode, editData, initialFormData, siteViewData])
  //-----------------------------------------------------------------------|

  // "change detected" logic for editing-form-data validation
  useEffect(() => {
    setChangeDetected(!isEqual(initialFormData, editData))
  }, [initialFormData, editData])
  //-----------------------------------------------------------------------|
  
  // sets created and modified date-times ------------------------------->
  useEffect(() => {
    let dateTime = [siteViewData?.created, siteViewData?.modified ?? siteViewData?.created];
    const data = formatDateTimeData(['created', 'modified'], dateTime, timezone);
    setDateTimes(data);
  }, [siteViewData?.created, siteViewData?.modified, timezone]);

  useEffect(() => {
    if (dateTimes) {
      setCreated(siteViewData?.created);
      setModified(siteViewData?.modified ?? siteViewData?.created)
    }
  }, [dateTimes, siteViewData])
  //-----------------------------------------------------------------------|

  // sets default and custom fields, and maintenance date ---------------->
  const setFields = useCallback((data, fieldType) => {
    if (data) {
      // loops through data and formats it for rendering
      const customModified = data?.map(field => {
        let object = {};
        // formats for photos
        if (field.field_name === "Photo") {
          object["file_url"] = field?.file_url || "";
        }
        // formatted object to return
        object = {
          ...object,
          field_name: field.field_name ?? "",
          value: field.value ?? "",
        }

        return object;
      });
      // dynamic 'setState' for default or custom fields
      fieldType(customModified);
    }
  }, []) //----------------------------------------------------------------|

  useEffect(() => {
    // sets default fields for this asset
    setFields(siteViewData?.defaults, setDefaultFields)

    // sets custom fields for this asset
    setFields(siteViewData?.custom, setCustomFields)

    // set next maintenance date
    if (siteViewData?.next_maintenance_date) {
      setNextMaintenanceDate(siteViewData?.next_maintenance_date || "");
    }
  }, [siteViewData, setFields])
  
  // set initialised data object for change detection and assigning values to form fields ---------------->
  useEffect(() => {
    if (siteViewData) {
      const { defaults, custom, next_maintenance_date } = siteViewData;
      
      // empty starting array to store data collectively
      const mappedInitialFormValues = [];

      // maps out initial form data of default fields
      if (defaults) {
        const defaultsMap = Object.values(defaults)?.map(item => (
          {
            id: item.field_uid,
            value: item.value,
            default: true,
            field_name: item.field_name,
            type: item?.type,
            ...(item?.type === 'dropdown' ? { options: item?.options } : {}),
          }
        ));
        defaultsMap.length > 0 && mappedInitialFormValues.push(...defaultsMap);
      }
      
      // maps out initial form data of custom fields
      if (custom) {
        const customMap = Object.values(custom)?.map(item => ({
          id: item.field_uid,
          value: item.value,
          default: false,
          field_name: item.field_name,
          type: item?.type,
          ...(item?.type === 'dropdown' ? { options: item?.options } : {}),
        }));
        customMap.length > 0 && mappedInitialFormValues.push(...customMap);
      }

      // adds next_maintenance_date to initial form data if other values exist 
      // (to avoid other values not being set in state)
      if (mappedInitialFormValues.length > 0) {
        mappedInitialFormValues.push({ field_name: "next_maintenance_date", value: next_maintenance_date ?? null });
      }

      // sets in state if not already set
      setInitialFormData(mappedInitialFormValues);
    }
  }, [
      // defaultFields, 
      // customFields,  
      siteViewData, 
      assetUid, 
      dataTypesAligned
    ]
  ) //-----------------------------------------------------------------------|

  // sets name and photo in edit data (has to find those fields from the edit data first)
  useEffect(() => {
    if (siteViewData) {
      // set name
      dataTypesAligned && editData && Array.isArray(editData) && setName(editData?.find((f) => f.field_name === "Name") ?? null)

      // set photo
      const photoField = Array.isArray(defaultFields) && defaultFields.find(field => field.field_name === "Photo");
      setPhoto(photoField?.file_url ?? "");
    }
  }, [siteViewData, defaultFields, dataTypesAligned, editData])
  //------------------------------------------------------------------------|

  // handleFieldChange callback for setting form field ---------------------------------------------->
  const handleFieldChange = useCallback((field, newValue) => {
    setEditData((prevData) =>
      prevData?.map((item) =>
        item.id === field.id ? { ...item, value: newValue } : item
      )
    );
  }, []);

  useEffect(() => {
    newPhotoFile && setEditData((prevData) =>
      prevData?.map((item) =>
        item.field_name === "Photo" ? { ...item, value: newPhotoFile } : item
      )
    );
  }, [newPhotoFile])
  //-----------------------------------------------------------------------|

  // map custom fields as field inputs
  const mapFields = useCallback((fieldsToMap) => {

    const formatOption = (data) => {
      return { label: data?.option ?? "", value: data?.option ?? "", colour: data?.colour ?? "transparent" }
    }

    const renderType = (field) => {
      const item = (Array.isArray(editData) && editData?.find((f) => f.id === field.id)) ?? {};
      switch (field?.type) {
        case "dropdown":
          const foundOption = field?.options && Object.values(field?.options).find(option => option?.option?.toString() === item?.value?.toString());
          const formattedOption = foundOption ? formatOption(foundOption) : { label: "", value: "", colour: "transparent" };
          const options = field?.options && Object.values(field?.options)?.map(option => formatOption(option));
          return (
            <CustomFieldDropdown
              key={field?.id}
              field={field}
              options={options}
              value={formattedOption}
              onChange={handleFieldChange}
              editMode={editMode}
            />
          )
        default:
          const textValue = item?.value ?? "";
          return (
            <CustomField
              key={field?.id}
              field={field}
              value={textValue}
              onChange={handleFieldChange}
              editMode={editMode}
            />
          )
      }
    }

    return fieldsToMap?.map((field) => renderType(field));
  }, [editData, handleFieldChange, editMode]);

  // map default fields that aren't "Name" or "Photo" as field inputs
  const mapRemainingDefaultFields = () => {
    if (editData.length > 0) {
      const skipFields = ['Name', 'Photo']
      let defaultsCopy = editData?.filter(field => 
        !(skipFields.includes(field.field_name)) 
        && field.default === true,
      )
      return mapFields(defaultsCopy);
    }
    return [];
  }

  const fileInputRef = useRef(null);
  const handlePhotoInputClick = () => {
    fileInputRef.current.click();
  }

  // manages fetch limit state to avoid excessive calls to endpoint
  const [fetchLimit, setFetchLimit] = useState(0);
  const [fetchButtonClickCounter, setFetchButtonClickCounter] = useState(0);
  const [timeTillFetchLimitReset, setTimeTillFetchLimitReset] = useState(0);
  useEffect(() => {
    // early return if fetch limit active
    if (fetchLimit !== 2) { return; }

    // notify user of refresh limit reach with TOAST notification ---->
    ButterToast.raise({
      content: (
        <div className="notes-limit-toast">
          <div className="notes-limit-toast__title">Notes refresh rate limit reached</div>
          <div className="notes-limit-toast__body">Please wait a moment before refreshing again.</div>
        </div>
      ),
      timeout: 2000,
      sticky: false,
    });
    // --------------------|
  }, [fetchLimit])

  useEffect(() => {
    if (fetchLimit !== 2) {
      setTimeTillFetchLimitReset(2000)
    };
  }, [fetchButtonClickCounter])

  useEffect(() => {
    // set timeout before resetting limit 
    setTimeout(() => {
      // checks state to avoid infinite re-rendering
      fetchLimit === 2 && setFetchLimit(0);
    }, [timeTillFetchLimitReset])
  }, [fetchLimit, timeTillFetchLimitReset])
  // -----------------------|

  // Below this width the chat auto-expands to full width (the side-pane
  // proportions get too cramped to be usable). Above it the chat stays a side
  // pane, with the full-screen toggle available to expand it manually.
  const isNarrowScreen = windowSize.width < 1000;
  const chatFullWidth = showUserActionChat && (isNarrowScreen || chatFullScreen);
  const contentColSpan = chatFullWidth ? 12 : true;
  const chatColSpan = showUserActionChat ? (chatFullWidth ? 12 : 4) : "auto";

  // Rendered elements ------>
  return (
    <div 
      className={`${renderOnLoad ? "d-none" : ""} ${darkMode ? "darkMode" : ""} w-100`} 
      id="site-asset-view-page" 
      data-intercom-target={"asset-view-wrapper"}
    >

    {/* Modal containing enlarged image when opened */}
    <Modal
      backdrop="static"
      centered
      keyboard={false}
      show={showAssetPhotoModal}
      size={photoFullScreen ? undefined : "lg"}
      dialogClassName={`asset-photo-modal ${photoFullScreen ? "asset-photo-modal-full-screen" : ""}`}
    >
      <Modal.Header
        closeButton
        onHide={minimisePhoto}
      >
        <h6>{name?.value}</h6>
        <div className="asset-photo-modal-controls d-flex align-items-center ml-auto mr-3">
          <Button
            variant="light"
            size="sm"
            title="Zoom out"
            disabled={photoZoom <= PHOTO_ZOOM_MIN}
            onClick={() => zoomPhoto(-PHOTO_ZOOM_STEP)}
          >
            <Icon className="fas fa-minus" />
          </Button>
          <Button
            variant="light"
            size="sm"
            className="zoom-level mx-1"
            title="Reset zoom and position"
            onClick={resetPhotoView}
          >
            {Math.round(photoZoom * 100)}%
          </Button>
          <Button
            variant="light"
            size="sm"
            title="Zoom in"
            disabled={photoZoom >= PHOTO_ZOOM_MAX}
            onClick={() => zoomPhoto(PHOTO_ZOOM_STEP)}
          >
            <Icon className="fas fa-plus" />
          </Button>
          <Button
            variant="light"
            size="sm"
            className="full-screen-toggle ml-2"
            title={photoFullScreen ? "Exit full screen" : "Full screen"}
            onClick={toggleFullScreenPhoto}
          >
            <Icon className={`fas ${photoFullScreen ? "fa-compress" : "fa-expand"}`} />
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body
        className={`asset-photo-modal-body d-flex justify-content-center align-items-center p-2 ${draggingPhoto ? "is-dragging" : ""}`}
      >
        <img
          className="view-asset-image-large p-1"
          src={photo}
          alt="site-asset"
          draggable={false}
          onMouseDown={startPhotoDrag}
          onTouchStart={startPhotoDrag}
          // the image is laid out fitted to its frame; the zoom scales it and
          // the drag offset moves it, neither of which affects the layout. The
          // translate is listed first so a drag tracks the pointer 1:1 at any
          // zoom level
          style={{
            transform: `translate(${photoOffset.x}px, ${photoOffset.y}px) scale(${photoZoom})`,
          }}
        />
      </Modal.Body>
    </Modal>

    <Container fluid>
      
      {/* Sticky Chat Icon — shows the unread-message count while the chat is
          closed and new notes arrive on the regular resync */}
      <div style={{ position: "sticky", top: "10px", height: 0, width: "100%", zIndex: 9999 }}>
        {!showAssetPhotoModal && <UnreadMessagesIcon count={unreadCount} />}
      </div>

      {/* clip horizontally only — "overflow: clip" on both axes was cutting off
          the top of the sticky chat pane. overflow-y: visible frees the vertical
          axis (clip, not scroll, so it doesn't break the pane's sticky). */}
      <Row className="flex-nowrap" style={{ overflowX: "clip", overflowY: "visible", paddingRight: "0px" }}>
        <Col
          className={`p-0 mr-1 ${chatFullWidth ? "d-none" : ""}`}
          xs={contentColSpan}
          lg={contentColSpan}
          md={contentColSpan}
          // min-width:0 lets this flex column shrink below its content's
          // intrinsic width (the hero card / asset table have a large
          // min-content). Without it, at ~1000–1200px the column refuses to
          // shrink and pushes the col-4 chat column — and its 30px toggle
          // strip — past the clipped row edge, hiding the button.
          style={{ minWidth: 0 }}
        >
          <Card id="asset-hero-card" className="pl-3">
            <Row className="align-items-center g-3 pl-2">

              {/* Photo */}
                {(photo || editMode) && (
                  <Col
                    xs={12} sm={6} md={3} lg={3}
                    className={`px-2 ${editMode ? "view-asset-image-edit mr-3" : ""}`}
                    onClick={editMode ? handlePhotoInputClick : (() => "")}
                  >
                    {editMode ?
                      <div>
                        <Tooltip
                          placement="bottom"
                          description={"Upload new image"}
                        >
                          <span>
                            <input
                              type="file"
                              className="file"
                              name="photo-edit-input"
                              id="photo-edit-input"
                              ref={fileInputRef}
                              onChange={onFileChange}
                            />
                            {previewImage ?
                              !photoFileInvalid ?
                                <img
                                  className={`view-asset-image img-fluid`}
                                  src={previewImage}
                                  alt={"New asset image placeholder"}
                                />
                              :
                              <span>
                                <Icon className="far fa-times-circle photo-edit-icon-invalid" />
                              </span>
                            :
                              <span>
                                <Icon className="fas fa-images photo-edit-icon" />
                              </span>
                            }
                          </span>
                        </Tooltip>
                      </div>
                    :
                      <Tooltip
                        placement="bottom"
                        description={"Enlarge image"}
                      >
                        <img
                          className={`view-asset-image img-fluid ${editMode ? "edit-photo-overlay" : ""}`}
                          src={photo}
                          alt={editMode ? "preview of new image" : "site asset image"}
                          onClick={editMode ? (() => "") : enlargePhoto}
                        />
                      </Tooltip>
                    }
                  </Col>
                )}

                {/* Text Fields */}
                <Col sm={12} md={6} lg={6} className={`name-date-section${editMode ? "-edit" : !(photo || previewImage) ? -" border-left" : ""}`}>
                  {/* Name */}
                  <div id="site-asset-name" className="detail-field p-0">
                    <div className="d-flex flex-row align-items-center">
                      <Form.Label className="form-input-labels m-0">Name</Form.Label>
                      {(!editMode && name?.value) && <CopyToClipboard className="m-0" id="name" copyText={name?.value} />}
                    </div>
                    {
                      !editMode ? 
                        <p className="site-details-read-only site-asset-name-value">{name?.value || ""}</p>
                      :
                        <CustomField
                          key={name?.id}
                          field={name}
                          value={name?.value ?? ""}
                          onChange={handleFieldChange}
                          editMode={editMode}
                          showLabel={false}
                          useCol={true}
                          colSizes={{
                            sm: 12,
                            md: 12,
                            lg: 12,
                          }}
                          customStyle={{ col: "pl-0 mt-1" }}
                        />
                      }
                  </div>

                  {/* Created */}
                  <div className="mb-2 assets-created-created">
                    <Form.Label className="form-input-labels m-0">Created</Form.Label>
                    <DateTimeFormatUtility
                      types={["created"]}
                      dateTime={[created ?? ""]}
                      timezone={timezone}
                      showTimezone={false}
                      windowSizeProp={windowSize}
                      layout="flex-row"
                      descriptionPrefix="Asset"
                      // tooltipPlacement="right"
                      minimised
                      showIcons={false}
                      // showClipboard={true}
                      // customClipboardLocation={false}
                      // showLabel={true}
                    />
                  </div>

                  {/* Modified */}
                  <div className="mb-2 assets-created-modified">
                    <Form.Label className="form-input-labels m-0">Modified</Form.Label>
                    <DateTimeFormatUtility
                      className="p-0 m-0"
                      types={["modified"]}
                      dateTime={[modified ?? ""]}
                      timezone={timezone}
                      showTimezone={false}
                      windowSizeProp={windowSize}
                      layout="flex-row"
                      descriptionPrefix="Asset"
                      // tooltipPlacement="right"
                      minimised
                      showIcons={false}
                      // showClipboard={true}
                      // customClipboardLocation={true}
                      // showLabel={true}
                    />
                  </div>
                </Col>
              
              {/* </div> */}
            </Row>
            <Form.Control.Feedback type="warning" className="trigger-feedback-invalid col p-0">
              <div className={`feedback-container-${photoFileInvalid ? "after" : "before"}`}>
                <div className="d-flex align-items-center flex-wrap justify-content-between">
                  <div className="feedback-text-stack col-12">
                    {photoFileInvalid}
                  </div>
                </div>
              </div>
            </Form.Control.Feedback>
          </Card>
          
          {/* Row 1 */}
          <Form.Text className="page-sub-header">Identification</Form.Text>
          <Row className="w-100 d-flex flex-row justify-content-between align-items-center mt-4 mb-4 g-3">
            {mapRemainingDefaultFields()}
          </Row>

          {/* Row 2 */}
          <Form.Text className="page-sub-header">Scheduling</Form.Text>
          <Row className="w-100 d-flex flex-row justify-content-between align-items-center mt-4 mb-4 g-3">
            <Col
              id="nextMaintenanceDate"
              xs={12} md={6} lg={6}
              className="detail-field"
            >
              <div className="d-flex flex-row">
                <Form.Label className="form-input-labels">Next Maintenance Date</Form.Label>
                {nextMaintenanceDate && <CopyToClipboard id={"nextMaintenanceDate"} copyText={nextMaintenanceDate} />}
              </div>
              <Form.Control
                data-intercom-target={"next-maintenance-date-input-field"}
                type="text"
                name="Custom Group Ref"
                className={"site-details-read-only"}
                value={nextMaintenanceDate ? nextMaintenanceDate : "N/A"}
                onChange={(e) => ''}
              />
            </Col>
          </Row>

          {/* Row 4 */}
          {customFields.length > 0 &&
            <div>
              <Form.Text className="page-sub-header">Custom Fields</Form.Text>
              <Row className="w-100 d-flex flex-row justify-content-between align-items-center mt-4 mb-4 g-3">
                {mapFields(editData?.filter(field => field.default === false))}
              </Row>
            </div>
          }

          {/* Asset Linking Table */}
          {isMounted &&
            <Row className="d-flex flex-row justify-content-between align-items-center mt-4 mb-0 mr-1 g-3">
              <Col xs={12} md={12} lg={12} className="pr-0">
                <AssetLinking 
                  timezone={timezone} 
                  setNavSiteViewName={setNavSiteViewName} 
                  setNavName={setNavName}
                  setSiteAssetView={setSiteAssetView} 
                  scrollToTop={scrollToTop}
                />
              </Col>
            </Row>
          }
          
        </Col>

        {/* User Action Chat */}
        <Col
          className="p-0"
          xs={chatColSpan}
          lg={chatColSpan}
          md={chatColSpan}
          style={{ minWidth: "30px", zIndex: "1000" }}
        >
          <div className="d-flex flex-row h-100 w-100">
            
            {/* Side Pane Content */}
            {/* {showUserActionChat && */}
              <UserActionsChat
                show={showUserActionChat}
                fullScreen={chatFullScreen}
                toggleFullScreen={() => setChatFullScreen(!chatFullScreen)}
                // hidden only on the (practically unreachable) auto-full-width
                // widths, where a manual toggle would be redundant
                showFullScreenToggle={!isNarrowScreen}
                lastUpdatedActionsLog={lastUpdatedActionsLog}
                setLastUpdatedActionsLog={setLastUpdatedActionsLog}
                setUnreadCount={setUnreadCount}
                fetchLimit={fetchLimit}
                setFetchLimit={setFetchLimit}
                fetchButtonClickCounter={fetchButtonClickCounter}
                setFetchButtonClickCounter={setFetchButtonClickCounter}
                siteViewData={siteViewData}
              />
            {/* } */}

            {/* rigid 30px strip: flex 0 0 30px so it never shrinks, and its own
                stacking context so it always paints above the sticky chat pane
                — keeps the toggle button fully visible at every width */}
            <div
              className="d-flex flex-column justify-content-end site-asset-view-side-pane"
              style={{ flex: "0 0 30px", position: "relative", zIndex: 2 }}
            >
              {/* Side Pane Button */}
              <Button
                // disabled={editMode}
                variant={showUserActionChat ? "primary" : "light"}
                onClick={() => toggleShowUserActionChat()}
                onMouseEnter={() => setHoveredSidePaneButton(true)}
                onMouseLeave={() => setHoveredSidePaneButton(false)}
                className={hoveredSidePaneButton ? "hovered" : ""}
                style={{
                  position: "sticky",
                  width: "30px",
                  minWidth: "30px",
                  top: "0",
                  height: "100%",
                  zIndex: "9999",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  borderTopLeftRadius: "0px",
                }}
              >
              </Button>
            </div>
          </div>
        </Col>
        
      </Row>
      
      <ButterToast position={{ vertical: POS_BOTTOM,  horizontal: POS_RIGHT }} style={{ bottom: "80px" }} /> 

    </Container>
    </div>
  )
}

export default SiteAssetView;